import React, { useRef } from "react";
import { noop } from "lodash";
import cn from "src/utils/cn";
import Icon from "src/components/Display/Icon/Icon";

interface IconProps {
  name: string;
  color?: string;
  style?: React.CSSProperties;
  className?: string;
  position?: "left" | "right";
  direction?: "up" | "down" | "left" | "right";
}

interface ButtonProps {
  id?: string;
  className?: string;
  label?: string;
  link?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  primary?: boolean;
  secondary?: boolean;
  destructive?: boolean;
  outline?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  shouldRender?: boolean;
  style?: React.CSSProperties;
  type?: "button" | "submit" | "reset";
  icon?: IconProps;
  "data-test-element"?: string;
  "data-testid"?: string;
  upperCase?: boolean;
  ariaHasPopUp?: string;
  omitDefaultStyles?: boolean;
  ariaExpanded?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  id = "",
  className = "",
  label = "",
  link = false,
  onClick = noop,
  onMouseEnter = noop,
  onMouseLeave = noop,
  primary = false,
  secondary = false,
  destructive = false,
  outline = false,
  disabled = false,
  fullWidth = false,
  shouldRender = true,
  style = {},
  icon = null,
  type = "button",
  "data-test-element": dataTestElement = null,
  "data-testid": dataTestId = null,
  upperCase = false,
  ariaHasPopUp,
  omitDefaultStyles = false,
  ariaExpanded = false,
  ...rest
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (disabled) {
      e.preventDefault();
    } else {
      onClick(e);
      buttonRef.current?.blur();
    }
  };

  const buttonClass = cn(
    "inline-block font-regular-font py-2 px-4 rounded border border-solid focus:outline-solid-offset", // Base classes
    {
      // Conditional classes
      "text-sm font-medium focus:border-focus-blue capitalize":
        !omitDefaultStyles,
      "bg-action-blue text-white border-transparent":
        primary && !omitDefaultStyles,
      "hover:bg-deep-blue": primary && !omitDefaultStyles && !disabled,
      "bg-white text-brand-blue border-light-border-blue":
        secondary && !omitDefaultStyles,
      "hover:bg-medium-fill-grey": secondary && !omitDefaultStyles && !disabled,
      "bg-transparent text-action-blue border-medium-border-blue":
        outline && !omitDefaultStyles,
      "hover:border-action-blue hover:text-deep-blue":
        outline && !omitDefaultStyles && !disabled,
      "bg-white border-button text-black":
        !primary && !secondary && !outline && !link && !omitDefaultStyles,
      "border-none bg-transparent text-action-blue p-4": link,
      "bg-red text-white border-transparent":
        !primary && !secondary && !outline && destructive && !omitDefaultStyles,
      "hover:bg-dark-red":
        !primary &&
        !secondary &&
        !outline &&
        destructive &&
        !disabled &&
        !omitDefaultStyles,
      "opacity-40 cursor-default": disabled,
      "cursor-pointer": !disabled,
      "w-full": fullWidth,
      uppercase: upperCase,
      "flex items-center justify-center": icon,
    },
    className // User-defined classes
  );

  const iconElement = icon?.name ? (
    <Icon
      color={icon?.color}
      icon={icon.name}
      style={icon?.style}
      className={icon?.className}
      direction={icon?.direction}
    />
  ) : null;

  if (!shouldRender) {
    return null;
  }

  return (
    <button
      id={id}
      ref={buttonRef}
      className={buttonClass}
      aria-haspopup={
        ariaHasPopUp as
          | boolean
          | "listbox"
          | "grid"
          | "menu"
          | "false"
          | "true"
          | "tree"
          | "dialog"
          | undefined
      }
      aria-label={label}
      onClick={handleClick}
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
      aria-disabled={disabled}
      aria-expanded={ariaExpanded}
      style={style}
      {...rest}
    >
      {iconElement && icon?.position === "left" && (
        <span style={{ color: 'inherit' }} className="inline-block mr-2 transform -translate-y-px">
          {iconElement}
        </span>
      )}
      {label}
      {iconElement && icon?.position === "right" && (
        <span style={{ color: 'inherit' }} className="inline-block ml-2 transform -translate-y-px">
          {iconElement}
        </span>
      )}
    </button>
  );
};

export default Button;
